import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, User, MapPin, Ruler, Weight, ArrowRight, MessageCircle, CheckCircle2, Globe, Landmark, ShieldCheck, Boxes, Plus, Trash2, Calendar, Clock, Truck, Copy, ClipboardPaste } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Combobox } from "../components/Combobox";
import { toast } from "sonner";
import { api } from "../api";
import { useApp } from "../context/AppContext";

export const SERVICE_OPTIONS = ["Air Cargo", "Sea Cargo", "Europe → Europe", "Sri Lanka → Europe"];

const emptyBox = () => ({ weight: "", length: "", width: "", height: "", contents: "" });

const empty = {
  serviceType: "", senderCountry: "", receiverCountry: "", sendingFor: "", insurance: "",
  pickupDate: "", pickupTime: "", sentBy: "", itemValue: "",
  boxes: [emptyBox()],
  senderName: "", senderId: "", senderPhone: "", senderAddress: "", senderCity: "", senderPostal: "",
  receiverName: "", receiverId: "", receiverPhone: "", receiverAddress: "", receiverCity: "", receiverPostal: "",
};

const BLANK_TEMPLATE = `Service: 
From: 
To: 
Sending for: 
Pickup date: 
Pickup time: 
Insurance: 
Item value: 

Box 1 | Weight:  | Dimensions:  x  x  | Contents: 

Sender name: 
Sender ID: 
Sender phone: 
Sender address: 
Sender city: 
Sender postal: 

Receiver name: 
Receiver ID: 
Receiver phone: 
Receiver address: 
Receiver city: 
Receiver postal: `;

function parseTemplate(text) {
  const out = { boxes: [] };
  const keyMap = {
    "service": "serviceType", "from": "senderCountry", "to": "receiverCountry",
    "sending for": "sendingFor", "pickup date": "pickupDate", "pickup time": "pickupTime",
    "insurance": "insurance", "item value": "itemValue",
    "sender name": "senderName", "sender id": "senderId", "sender phone": "senderPhone",
    "sender address": "senderAddress", "sender city": "senderCity", "sender postal": "senderPostal",
    "receiver name": "receiverName", "receiver id": "receiverId", "receiver phone": "receiverPhone",
    "receiver address": "receiverAddress", "receiver city": "receiverCity", "receiver postal": "receiverPostal",
  };
  text.split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    if (/^box\s*\d*/i.test(line) && /weight|dimension|content/i.test(line)) {
      const box = emptyBox();
      const w = line.match(/weight\s*:\s*([^|]*)/i);
      const d = line.match(/dimensions?\s*:\s*([^|]*)/i);
      const c = line.match(/contents?\s*:\s*(.*)$/i);
      if (w) box.weight = w[1].trim();
      if (d) {
        const dims = d[1].split(/[x×]/i).map((s) => s.trim());
        box.length = dims[0] || ""; box.width = dims[1] || ""; box.height = dims[2] || "";
      }
      if (c) box.contents = c[1].trim();
      if (box.weight || box.length || box.contents) out.boxes.push(box);
      return;
    }
    const m = line.match(/^([^:]+):\s*(.*)$/);
    if (!m) return;
    const key = m[1].trim().toLowerCase();
    const val = m[2].trim();
    const field = keyMap[key];
    if (!field || !val) return;
    if (field === "serviceType") {
      const found = SERVICE_OPTIONS.find((o) => o.toLowerCase().replace(/[^a-z]/g, "") === val.toLowerCase().replace(/[^a-z]/g, ""))
        || SERVICE_OPTIONS.find((o) => o.toLowerCase().includes(val.toLowerCase()) || val.toLowerCase().includes(o.toLowerCase().split(" ")[0]));
      out[field] = found || val;
    } else out[field] = val;
  });
  return out;
}

export default function SendPackage() {
  const { settings, user, countries, insurance, t } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({ ...empty, senderName: user?.name || "", senderPhone: user?.phone || "" });
  const [done, setDone] = useState(false);
  const [waNumber, setWaNumber] = useState(settings.whatsapp);
  const [staff, setStaff] = useState([]);
  const [paste, setPaste] = useState("");
  const isStaff = user?.role === "admin" || user?.role === "employee";

  useEffect(() => {
    if (isStaff) api.getStaffNames().then(setStaff).catch(() => {});
  }, [isStaff]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setBox = (i, k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setF((prev) => ({ ...prev, boxes: prev.boxes.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  };
  const addBox = () => setF((prev) => ({ ...prev, boxes: [...prev.boxes, emptyBox()] }));
  const removeBox = (i) => setF((prev) => ({ ...prev, boxes: prev.boxes.length > 1 ? prev.boxes.filter((_, idx) => idx !== i) : prev.boxes }));
  const options = countries.map((c) => ({ value: c.name, label: c.name, flag: c.flag }));

  const totalWeight = f.boxes.reduce((sum, b) => sum + (parseFloat(b.weight) || 0), 0);

  const insuranceCards = [{ id: "none", name: "No insurance", price: "Free", coverage: "—", desc: "No cover — you accept the risk for this shipment." }, ...insurance];

  const applyTemplate = () => {
    if (!paste.trim()) { toast.error("Paste a filled-in template first"); return; }
    const parsed = parseTemplate(paste);
    setF((prev) => ({
      ...prev, ...parsed,
      boxes: parsed.boxes && parsed.boxes.length ? parsed.boxes : prev.boxes,
    }));
    toast.success("Form filled from your template. Please review before sending.");
  };

  const buildWaText = () => {
    const insPrice = (insuranceCards.find((i) => i.name === f.insurance) || {}).price || "";
    const boxLines = f.boxes.map((b, i) =>
      ` Box ${i + 1} — ${b.weight || "?"} kg — ${b.length || "?"}x${b.width || "?"}x${b.height || "?"} cm — ${b.contents || "-"}`).join("\n");
    return encodeURIComponent(
`Hi Smart Logistics!
I'd like to send a parcel.

New Parcel Request
Service: ${f.serviceType || "-"}
From: ${f.senderCountry}  →  To: ${f.receiverCountry}
Sending for: ${f.sendingFor || "-"}
Pickup: ${f.pickupDate || "-"}${f.pickupTime ? ` at ${f.pickupTime}` : ""}
${f.sentBy ? `Sent by (staff): ${f.sentBy}\n` : ""}
Boxes: ${f.boxes.length}
${boxLines}
Total weight: ${totalWeight} kg
Declared item value: ${f.itemValue || "-"}
Insurance: ${f.insurance || "Not selected"}${insPrice ? ` (${insPrice})` : ""}

SENDER
Name: ${f.senderName}
ID: ${f.senderId}
Phone: ${f.senderPhone}
Country: ${f.senderCountry}
Address: ${f.senderAddress}
City: ${f.senderCity}
Postal code: ${f.senderPostal}

RECEIVER
Name: ${f.receiverName}
ID: ${f.receiverId}
Phone: ${f.receiverPhone}
Country: ${f.receiverCountry}
Address: ${f.receiverAddress}
City: ${f.receiverCity}
Postal code: ${f.receiverPostal}

Payment: Bank transfer — please share bank details.`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.serviceType) { toast.error("Please choose a service type"); return; }
    if (!f.pickupDate) { toast.error("Please choose a pickup date"); return; }
    if (!f.boxes.some((b) => b.weight)) { toast.error("Add at least one box with a weight"); return; }
    const required = ["senderCountry", "receiverCountry", "senderName", "senderPhone", "senderAddress", "receiverName", "receiverPhone", "receiverAddress"];
    if (required.some((k) => !f[k])) { toast.error("Please fill all required fields"); return; }
    const first = f.boxes[0] || {};
    const insPrice = (insuranceCards.find((i) => i.name === f.insurance) || {}).price || "";
    const payload = {
      ...f,
      userEmail: user?.email || "guest",
      insurancePrice: insPrice,
      weight: String(totalWeight),
      length: first.length, width: first.width, height: first.height,
      contents: f.boxes.map((b, i) => `Box ${i + 1}: ${b.contents || "-"}`).join("; "),
    };
    try {
      const res = await api.addSubmission(payload);
      setWaNumber(res.whatsapp);
      setDone(true);
      toast.success("Request saved! Opening WhatsApp…");
      window.open(`https://wa.me/${res.whatsapp}?text=${buildWaText()}`, "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    }
  };

  if (done) {
    return (
      <section className="max-w-2xl mx-auto px-5 py-24 text-center animate-fade-up">
        <div className="w-16 h-16 rounded-2xl orange-gradient grid place-items-center mx-auto orange-glow"><CheckCircle2 className="w-9 h-9 text-white" /></div>
        <h1 className="font-display font-bold text-3xl mt-6">Request received!</h1>
        <p className="text-neutral-600 mt-3">Your parcel details are with our team. We've opened WhatsApp so you can confirm pickup and receive bank-transfer payment details. We'll arrange collection from your door.</p>
        <div className="flex flex-wrap gap-3 justify-center mt-8">
          <Button data-testid="parcel-reopen-whatsapp" className="orange-gradient text-white border-0 gap-2 hover:opacity-90" onClick={() => window.open(`https://wa.me/${waNumber}?text=${buildWaText()}`, "_blank")}><MessageCircle className="w-4 h-4" /> Reopen WhatsApp</Button>
          <Button variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04]" onClick={() => { setF({ ...empty, boxes: [emptyBox()] }); setPaste(""); setDone(false); }}>Send another</Button>
          {user && <Button variant="ghost" onClick={() => nav("/dashboard")}>View my requests</Button>}
        </div>
      </section>
    );
  }

  const field = (label, k, placeholder, req) => (
    <div>
      <Label className="text-neutral-700 text-sm">{label} {req && <span className="text-orange-500">*</span>}</Label>
      <Input value={f[k]} onChange={set(k)} placeholder={placeholder} className="bg-black/[0.03] border-black/10 mt-1.5" />
    </div>
  );

  return (
    <div>
      <section className="relative grid-bg border-b border-black/10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-orange-600/15 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 py-16 relative animate-fade-up">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">{t("send.eyebrow")}</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-3">{t("send.title")}</h1>
          <p className="text-neutral-600 mt-4 max-w-2xl text-lg">{t("send.subtitle")}</p>
        </div>
      </section>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-5 py-14 space-y-8">
        {/* Quick fill from template — staff only */}
        {isStaff && (
        <div className="glass rounded-2xl p-6" data-testid="template-fill-card">
          <div className="flex items-center gap-2 mb-2"><ClipboardPaste className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Quick fill</h2></div>
          <p className="text-sm text-neutral-600 mb-4">Paste a filled-in template to fill the whole form at once. Copy the blank format, fill it in, then paste it back here.</p>
          <Textarea value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={BLANK_TEMPLATE} data-testid="template-paste-input" className="bg-black/[0.03] border-black/10 font-mono text-xs min-h-40" />
          <div className="flex flex-wrap gap-2 mt-3">
            <Button type="button" onClick={applyTemplate} data-testid="template-fill-btn" className="orange-gradient text-white border-0 gap-2 hover:opacity-90 h-9"><ClipboardPaste className="w-4 h-4" /> Fill form from template</Button>
            <Button type="button" variant="outline" data-testid="template-copy-btn" onClick={() => { try { navigator.clipboard?.writeText(BLANK_TEMPLATE).then(() => toast.success("Blank template copied")).catch(() => toast.error("Copy failed — select the text and copy manually")); } catch { toast.error("Copy failed — select the text and copy manually"); } }} className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2 h-9"><Copy className="w-4 h-4 text-orange-500" /> Copy blank template</Button>
          </div>
        </div>
        )}

        {/* Service type */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><Truck className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Service</h2></div>
          <Label className="text-neutral-700 text-sm">Cargo / service type <span className="text-orange-500">*</span></Label>
          <div className="mt-1.5">
            <Select value={f.serviceType} onValueChange={(v) => setF({ ...f, serviceType: v })}>
              <SelectTrigger data-testid="service-type-select" className="bg-black/[0.03] border-black/10 max-w-md"><SelectValue placeholder="Choose a service" /></SelectTrigger>
              <SelectContent>{SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`service-opt-${o.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Route countries */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><Globe className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Where to & from</h2></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-neutral-700 text-sm">Sender's country <span className="text-orange-500">*</span></Label>
              <div className="mt-1.5"><Combobox options={options} value={f.senderCountry} onChange={set("senderCountry")} placeholder="Search sending country…" /></div>
            </div>
            <div>
              <Label className="text-neutral-700 text-sm">Receiver's country <span className="text-orange-500">*</span></Label>
              <div className="mt-1.5"><Combobox options={options} value={f.receiverCountry} onChange={set("receiverCountry")} placeholder="Search destination country…" /></div>
            </div>
          </div>
          <div className="mt-4"><Label className="text-neutral-700 text-sm">Who is the parcel for? (relationship / purpose)</Label><Input value={f.sendingFor} onChange={set("sendingFor")} placeholder="e.g. my mother, gift, documents" className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
        </div>

        {/* Boxes */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-2"><Boxes className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Boxes</h2>{totalWeight > 0 && <span className="text-sm text-neutral-500">· {f.boxes.length} box{f.boxes.length > 1 ? "es" : ""}, {totalWeight} kg total</span>}</div>
            <Button type="button" onClick={addBox} data-testid="add-box-btn" variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50 gap-1.5 h-9"><Plus className="w-4 h-4" /> Add box</Button>
          </div>
          <div className="space-y-5">
            {f.boxes.map((b, i) => (
              <div key={i} className="rounded-xl border border-black/10 bg-black/[0.02] p-4" data-testid={`box-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-sm flex items-center gap-1.5"><Package className="w-4 h-4 text-orange-500" /> Box {i + 1}</p>
                  {f.boxes.length > 1 && <Button type="button" onClick={() => removeBox(i)} data-testid={`remove-box-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                  <div><Label className="text-neutral-700 text-sm flex items-center gap-1.5"><Weight className="w-3.5 h-3.5" /> Weight (kg) <span className="text-orange-500">*</span></Label><Input value={b.weight} onChange={setBox(i, "weight")} type="number" placeholder="12" data-testid={`box-${i}-weight`} className="bg-white border-black/10 mt-1.5" /></div>
                  <div><Label className="text-neutral-700 text-sm flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> Length (cm)</Label><Input value={b.length} onChange={setBox(i, "length")} type="number" data-testid={`box-${i}-length`} className="bg-white border-black/10 mt-1.5" /></div>
                  <div><Label className="text-neutral-700 text-sm">Width (cm)</Label><Input value={b.width} onChange={setBox(i, "width")} type="number" data-testid={`box-${i}-width`} className="bg-white border-black/10 mt-1.5" /></div>
                  <div><Label className="text-neutral-700 text-sm">Height (cm)</Label><Input value={b.height} onChange={setBox(i, "height")} type="number" data-testid={`box-${i}-height`} className="bg-white border-black/10 mt-1.5" /></div>
                </div>
                <div className="mt-3"><Label className="text-neutral-700 text-sm">What's inside this box?</Label><Textarea value={b.contents} onChange={setBox(i, "contents")} placeholder="e.g. clothes, food items, documents" data-testid={`box-${i}-contents`} className="bg-white border-black/10 mt-1.5 min-h-16" /></div>
              </div>
            ))}
          </div>
          <div className="mt-4 max-w-md"><Label className="text-neutral-700 text-sm flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Total item value (for insurance)</Label><Input value={f.itemValue} onChange={set("itemValue")} placeholder="e.g. €250" data-testid="item-value-input" className="bg-black/[0.03] border-black/10 mt-1.5" /><p className="text-xs text-neutral-500 mt-1.5">The approximate value of all contents — helps us insure your parcel correctly.</p></div>
        </div>

        {/* Pickup */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><Calendar className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Pickup schedule</h2></div>
          <div className="grid md:grid-cols-2 gap-4 max-w-xl">
            <div><Label className="text-neutral-700 text-sm flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Pickup date <span className="text-orange-500">*</span></Label><Input value={f.pickupDate} onChange={set("pickupDate")} type="date" data-testid="pickup-date-input" className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
            <div><Label className="text-neutral-700 text-sm flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Preferred time</Label><Input value={f.pickupTime} onChange={set("pickupTime")} type="time" data-testid="pickup-time-input" className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
          </div>
        </div>

        {/* Staff-only: Sent by */}
        {isStaff && (
          <div className="glass rounded-2xl p-6 border border-orange-500/20" data-testid="sent-by-card">
            <div className="flex items-center gap-2 mb-2"><User className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Sent by <span className="text-sm font-normal text-neutral-500">· staff only</span></h2></div>
            <p className="text-sm text-neutral-600 mb-4">Pick who is taking this parcel in — used for internal analytics. Customers don't see this.</p>
            <div className="max-w-md">
              <Select value={f.sentBy} onValueChange={(v) => setF({ ...f, sentBy: v })}>
                <SelectTrigger data-testid="sent-by-select" className="bg-black/[0.03] border-black/10"><SelectValue placeholder="Choose staff member" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.email} value={s.name} data-testid={`sent-by-opt-${s.email}`}>{s.name}{s.role === "admin" ? " (admin)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Sender */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><User className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Sender details</h2></div>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Full name", "senderName", "Sender's full name", true)}
            {field("ID / Passport number", "senderId", "ID number")}
            {field("Phone number", "senderPhone", "+39 …", true)}
            <div><Label className="text-neutral-700 text-sm flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Pickup address <span className="text-orange-500">*</span></Label><Input value={f.senderAddress} onChange={set("senderAddress")} placeholder="Street, building" className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
            {field("City", "senderCity", "City")}
            {field("Postal / ZIP code", "senderPostal", "e.g. 20100")}
          </div>
        </div>

        {/* Receiver */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><User className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Receiver details</h2></div>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Full name", "receiverName", "Receiver's full name", true)}
            {field("ID / Passport number", "receiverId", "ID number")}
            {field("Phone number", "receiverPhone", "+94 …", true)}
            <div><Label className="text-neutral-700 text-sm flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Delivery address <span className="text-orange-500">*</span></Label><Input value={f.receiverAddress} onChange={set("receiverAddress")} placeholder="Street, building" className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
            {field("City", "receiverCity", "City")}
            {field("Postal / ZIP code", "receiverPostal", "e.g. 00100")}
          </div>
        </div>

        {/* Insurance */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><ShieldCheck className="w-5 h-5 text-orange-500" /><h2 className="font-display font-semibold text-xl">Insurance cover</h2></div>
          <div className="grid md:grid-cols-3 gap-4">
            {insuranceCards.map((ins) => {
              const active = f.insurance === ins.name;
              return (
                <button type="button" key={ins.id} data-testid={`insurance-${ins.id}`} onClick={() => setF({ ...f, insurance: ins.name })}
                  className={`text-left rounded-2xl p-5 border transition-all ${active ? "border-orange-500 bg-orange-500/[0.06] ring-2 ring-orange-500/30" : "border-black/10 bg-black/[0.02] hover:border-orange-500/50"}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-display font-semibold">{ins.name}</p>
                    <span className={`w-5 h-5 rounded-full grid place-items-center ${active ? "orange-gradient" : "border border-neutral-300"}`}>{active && <CheckCircle2 className="w-4 h-4 text-white" />}</span>
                  </div>
                  <p className="text-orange-600 font-semibold mt-2">{ins.price}</p>
                  <p className="text-xs text-neutral-500 mt-1">Cover {ins.coverage}</p>
                  <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{ins.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Payment note */}
        <div className="glass rounded-2xl p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg orange-gradient grid place-items-center shrink-0"><Landmark className="w-5 h-5 text-white" /></div>
          <div><p className="font-semibold text-sm">Payment by bank transfer</p><p className="text-sm text-neutral-600 mt-1">Once you submit, our team shares the bank-transfer details over WhatsApp to confirm your booking.</p></div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between glass rounded-2xl p-6">
          <p className="text-sm text-neutral-600">Your details are shared with our team and sent to the WhatsApp line handling your route.</p>
          <Button type="submit" data-testid="send-parcel-submit" className="orange-gradient text-white border-0 h-12 px-8 gap-2 hover:opacity-90 shrink-0">{t("send.submit")} <ArrowRight className="w-4 h-4" /></Button>
        </div>
      </form>
    </div>
  );
}
